import { ModalProps, ModalService, ThemeTonalRange } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';
import { RequestEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AuthenticationService, HistoryService, HttpService, JWTToken, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { HttpError } from '@philips-internal/oswald-ui-core/lib/services/http-service';
import { LoginAPI } from '@philips-internal/oswald-ui-foundation';
import { generateUUID, isMobile } from '@philips-internal/oswald-ui-utils';
import React from 'react';
import { getFoundationDataContext } from '../app/bootstrap-foundation';
import { FragmentNames } from '../codegen';
import { HttpStatus } from '../constants/HttpStatus';
import TokenConstants from '../constants/TokenConstants';
import { APPLICATION_ENDPOINTS } from '../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../constants/application-routes';
import { closeToastNotification, showToastNotification, showToastNotificationError } from './toast-notification-utils';

interface Resp {
  [key: string]: string;
}

interface ErrorMessageProps {
  isExpiredPassword: boolean;
  message: string;
}

interface ReturnAuthenticateValidationRequired{
  message: string;
  messageCode: number;
  phoneNumber: string;
  recoveryType: string;
  username: string;
}

interface WebLabCreationRequest{
  allowCreate: boolean,
  cdPessoaFisica: string
  username: string;
}

export class LoginUtils {

  static handleLoginError(event: RequestEvent<Error | Record<string, string>>): void {
    const api = event.target as unknown as LoginAPI;
    const error = event.response as unknown as HttpError;
    if (error.response?.status === HttpStatus.FORBIDDEN) {
      error.response?.json().then(res=> {
        const { phoneNumber, recoveryType, username }: ReturnAuthenticateValidationRequired = res;
        if(recoveryType === 'S'){
          const applicationContext = getFoundationDataContext();
          applicationContext.set( {'dsLogin': username} );
          applicationContext.set( {'phoneNumber': phoneNumber} );
          this.openModalConfirmationSmsCode();
        } else if(recoveryType === 'E'){
          api.removeNotification();
          const toastId = 'notification-user-inactive-recovery-type-email';
          showToastNotification({
            action: <a onClick={() => this.resendEmailActivationLink(res.username, toastId)}>
              {MessageService.translate('patient.activate.email.resend')}
            </a>,
            highlight: true,
            id: toastId,
            level: ToastLevel.ERROR,
            message:MessageService.translate('patient.inactive.description'),
            title:MessageService.translate('patient.inactive')
          });
        } else if (!recoveryType) {
          const { message } = LoginUtils.getErrorMessage(res, username);
          api.removeNotification();
          showToastNotificationError(message, undefined, true);
        }
      });
    } else if(error.response?.status === HttpStatus.UNAUTHORIZED){
      error.response?.json().then(res=> {
        const { allowCreate, cdPessoaFisica, username }: WebLabCreationRequest = res;
        if(allowCreate){
          this.openModalCreateUserFromWebLaboratory(cdPessoaFisica);
        }else{
          const { message }  = LoginUtils.getErrorMessage(res, username);
          api.setNotification({
            highlight: false,
            level: ToastLevel.ERROR,
            message,
            title: ' ',
          });
        }
      });
    } else if (error.response != null) {
      error.response.json().then((resp: Resp) => {
        const { message, isExpiredPassword }  = LoginUtils.getErrorMessage(resp);
        if(isExpiredPassword) {
          api.removeNotification();
          return;
        }
        api.setNotification({
          highlight: false,
          level: ToastLevel.ERROR,
          message
        });
      });
    }
  }

  static loginSucess(): void {
    const token = AuthenticationService.getJWTToken() as JWTToken;
    const patientId: string = token.claims[TokenConstants.PATIENT_ID] as string;

    HttpService.post(APPLICATION_ENDPOINTS.CHECK_FOR_CREATE_REVISION_PERSONAL_DATA_NOTIFICATIONS, { patientId } );
  }

  static getErrorMessage(resp: Resp, username?: string): ErrorMessageProps {
    const { message, messageCode } = resp;
    const loginExpiredCode = '870103';

    if (username && loginExpiredCode.match(messageCode)) {
      const msg = MessageService.translate('patient.login.password-expired');
      LoginUtils.openRenewPasswordModal(username, msg);

      return {
        isExpiredPassword: true,
        message: msg,
      }
    }

    if (!message && !messageCode) {
      return {
        isExpiredPassword: false,
        message: Object.keys(resp).reduce((ret, value) => `${ret} ${MessageService.translate(resp[value])}`, '')
      }
    }

    return {
      isExpiredPassword: false,
      message: message,
    };
  }

  static openRenewPasswordModal(username: string, message: string): void {
    const modalProps: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { message, username },
      focusOnOpen: true,
      fragment: 'change-expired-password',
      onCancel: () => '',
      onSubmit: () => '',
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: 'patient.change.password',
      uuid: generateUUID(),
    }

    isMobile() ? ModalService.openFullscreen(modalProps) : ModalService.openDefault(modalProps);
  }

  private static openModalConfirmationSmsCode(): void {
    const modalProps : ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: true,
      fragment:'account-creation-confirmation-sms-code',
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: MessageService.translate('account.confirmation.modal.title'),
      uuid: generateUUID(),
    };

    if(isMobile()){
      ModalService.openFullscreen(modalProps);
    }else{
      ModalService.openDialog(modalProps);
    }
  }

  private static openModalCreateUserFromWebLaboratory(cdPessoaFisica: string):void{
    const modalProps: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { cdPessoaFisica },
      focusOnOpen: true,
      fragment: FragmentNames.CREATE_USER_FROM_WEB_LABORATORY,
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: MessageService.translate('patient.web.lab.password.reset'),
      uuid: generateUUID(),
    }

    if(isMobile()){
      ModalService.openFullscreen(modalProps);
    }else{
      ModalService.openDialog(modalProps);
    }
  }

  private static resendEmailActivationLink(username: string, id: string): void {
    HttpService.post(APPLICATION_ENDPOINTS.RESEND_ACTIVATION_MAIL_BY_USERNAME, { username })
      .then(() => {
        closeToastNotification(id);
        HistoryService.push(APPLICATION_ROUTES.ACCOUNT_CREATION_COMPLETED);
      });
  }
}
